// src/PatronManagement.js
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./contexts/AuthContext";
import PatronForm from "./PatronForm";
import PatronCredentialsModal from "./components/CredentialManagement/modals/PatronCredentialsModal";
import AddPhysicalCredentialModal from "./components/PatronManagementView/AddPhysicalCredentialModal";
import QuickCreateCredentialModal from "./components/PatronManagementView/QuickCreateCredentialModal";
import ConfirmActionModal from "./ConfirmActionModal";
import PatronLimitModal from "./PatronLimitModal";
import config from "./config";
import logger from "./utils/logger";
import "./PatronManagement.css";

const PatronManagement = ({
  organizationId,
  organizationName,
  organizations,
  devices,
  pendingDeletionPatrons,
  setPendingDeletionPatrons,
  pendingDeletionCredentials,
  setPendingDeletionCredentials,
  pendingAdditionCredentials,
  setPendingAdditionCredentials,
  pendingFullCredentialDeletions,
  setPendingFullCredentialDeletions,
  devStreamEvents = [],
  lastCredentialDownload,
  lastCredentialProvisioned,
  lastCredentialRemoved,
}) => {
  const { getAccessToken } = useAuth();
  const [patrons, setPatrons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPatron, setEditingPatron] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedPatron, setSelectedPatron] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    patron: null,
  });
  const [isDeletingPatron, setIsDeletingPatron] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showAddPhysicalCredentialModal, setShowAddPhysicalCredentialModal] =
    useState(false);
  const [patronForPhysicalCred, setPatronForPhysicalCred] = useState(null);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  // const [totalPatronCount, setTotalPatronCount] = useState(0);
  const itemsPerPage = 25;

  const loadPatrons = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      let url = `${config.apiEndpoint}/patrons?orgId=${organizationId}&limit=${itemsPerPage}`;

      if (currentPage > 1 && lastEvaluatedKey) {
        url += `&lastEvaluatedKey=${lastEvaluatedKey}`;
      }

      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const accessToken = await getAccessToken();
      const response = await fetch(url, {
        headers: {
          "x-api-key": config.apiKey,
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load patrons: ${response.statusText}`);
      }

      const data = await response.json();
      setPatrons(data.patrons || []);
      setLastEvaluatedKey(data.lastEvaluatedKey);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      logger.error("Error loading patrons:", err);
      if (
        err.message.includes("Failed to fetch") ||
        err.message.includes("404")
      ) {
        setError(
          "Patron management backend is not deployed yet. Please deploy the backend infrastructure first.",
        );
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [
    organizationId,
    currentPage,
    lastEvaluatedKey,
    searchTerm,
    itemsPerPage,
    getAccessToken,
  ]);

  useEffect(() => {
    if (organizationId) {
      loadPatrons();
    }
  }, [organizationId, loadPatrons]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setLastEvaluatedKey(null);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadPatrons();
  };

  const fetchTotalPatronCount = async () => {
    if (!organizationId) return 0;

    try {
      // Fetch all patrons without limit to get total count
      const response = await fetch(
        `${config.apiEndpoint}/patrons?orgId=${organizationId}&limit=1000`,
        {
          headers: {
            "x-api-key": config.apiKey,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch patron count: ${response.statusText}`);
      }

      const data = await response.json();
      const count = data.patrons ? data.patrons.length : 0;
      // setTotalPatronCount(count);
      return count;
    } catch (err) {
      logger.error("Error fetching total patron count:", err);
      return 0;
    }
  };

  const handleCreatePatron = async () => {
    // Check patron limit before opening the form
    const count = await fetchTotalPatronCount();

    if (count >= config.patronLimit) {
      setShowLimitModal(true);
      return;
    }

    setEditingPatron(null);
    setIsFormOpen(true);
  };

  const handleQuickCreate = async () => {
    const count = await fetchTotalPatronCount();
    if (count >= config.patronLimit) {
      setShowLimitModal(true);
      return;
    }
    setIsQuickCreateOpen(true);
  };

  const handleEditPatron = (patron) => {
    setEditingPatron(patron);
    setIsFormOpen(true);
  };

  const handleDeletePatron = async (patron) => {
    setDeleteConfirmModal({
      isOpen: true,
      patron: patron,
    });
  };

  const handleConfirmDeletePatron = async () => {
    const patron = deleteConfirmModal.patron;
    if (!patron) return;

    setIsDeletingPatron(true);

    try {
      // First, get all credentials for this patron to add them to pending state
      const accessToken = await getAccessToken();
      const credentialsResponse = await fetch(
        `${config.apiEndpoint}/patrons/${patron.orgId}/${patron.patronId}/credentials`,
        {
          headers: {
            "x-api-key": config.apiKey,
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (credentialsResponse.ok) {
        const credentialsData = await credentialsResponse.json();
        // Add all credential IDs to pending deletion
        if (
          credentialsData.credentials &&
          Array.isArray(credentialsData.credentials)
        ) {
          credentialsData.credentials.forEach((cred) => {
            setPendingDeletionCredentials((prev) => new Set(prev).add(cred.id));
          });
        }
      }

      // Add patron to pending deletion
      setPendingDeletionPatrons((prev) => new Set(prev).add(patron.patronId));

      const response = await fetch(
        `${config.apiEndpoint}/patrons/${patron.orgId}/${patron.patronId}`,
        {
          method: "DELETE",
          headers: {
            "x-api-key": config.apiKey,
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to delete patron: ${response.statusText}`);
      }

      // Close modal and reload patrons
      setDeleteConfirmModal({ isOpen: false, patron: null });
      loadPatrons();
    } catch (err) {
      logger.error("Error deleting patron:", err);
      alert(`Failed to delete patron: ${err.message}`);
    } finally {
      setIsDeletingPatron(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingPatron(null);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingPatron(null);
    loadPatrons();
  };

  const handlePhotoClick = (photoUrl) => {
    setSelectedPhoto(photoUrl);
  };

  const handleImageError = (e, patron) => {
    // Replace broken image with initials fallback
    const initials = `${patron.firstName?.[0] || ""}${
      patron.lastName?.[0] || ""
    }`.toUpperCase();
    const canvas = document.createElement("canvas");
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext("2d");

    // Draw circular background
    ctx.fillStyle = "#667eea";
    ctx.beginPath();
    ctx.arc(50, 50, 50, 0, Math.PI * 2);
    ctx.fill();

    // Draw initials
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 40px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(initials, 50, 50);

    e.target.src = canvas.toDataURL();
    e.target.onerror = null; // Prevent infinite loop

    logger.warn(
      `Failed to load image for ${patron.email}, using initials fallback. URL: ${patron.badgePhotoUrl}`,
    );
  };

  const handleViewCredentials = (patron) => {
    setSelectedPatron(patron);
    setShowCredentialsModal(true);
  };

  const handleCloseCredentialsModal = () => {
    setShowCredentialsModal(false);
    setSelectedPatron(null);
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      setLastEvaluatedKey(null); // Reset to start of page
    }
  };

  if (!organizationId) {
    return (
      <div className="patron-management">
        <div className="no-org-message">
          <p>Please select an organization to manage patrons.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="patron-management">
      <div className="patron-header">
        <h1>Patron Management</h1>
        <p className="org-name">Organization: {organizationName}</p>
      </div>

      <div className="patron-controls">
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <button type="submit" className="search-button">
            🔍 Search
          </button>
        </form>
        <div className="patron-action-buttons">
          <button className="create-button" onClick={handleCreatePatron}>
            ➕ Create Patron
          </button>
          <button
            className="quick-create-button"
            onClick={handleQuickCreate}
            disabled={loading}
          >
            ⚡︎➕ Quick-Create Credential
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={loadPatrons}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Loading patrons...</p>
        </div>
      ) : patrons.length === 0 ? (
        <div className="no-patrons-message">
          <p>No patrons found.</p>
          <button onClick={handleCreatePatron}>Create your first patron</button>
        </div>
      ) : (
        <>
          <div className="patrons-table-container">
            <table className="patrons-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Patron Number</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>NFC Bit Format</th>
                  <th>Mobile Number</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {patrons.map((patron) => {
                  const isPendingDeletion = pendingDeletionPatrons.has(
                    patron.patronId,
                  );
                  return (
                    <tr
                      key={patron.patronId}
                      className={isPendingDeletion ? "pending-deletion" : ""}
                      style={isPendingDeletion ? { position: "relative" } : {}}
                    >
                      {isPendingDeletion && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(2px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            pointerEvents: "none",
                            borderRadius: "4px",
                          }}
                        >
                          <div
                            style={{
                              color: "#fff",
                              fontSize: "1.1rem",
                              fontWeight: "bold",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          >
                            Pending Deletion...
                          </div>
                        </div>
                      )}
                      <td>
                        <img
                          src={patron.badgePhotoUrl}
                          alt={`${patron.firstName} ${patron.lastName}`}
                          className="patron-thumbnail"
                          onClick={() => handlePhotoClick(patron.badgePhotoUrl)}
                          onError={(e) => handleImageError(e, patron)}
                        />
                      </td>
                      <td className="patron-number-cell">
                        {patron.patronNumber || "N/A"}
                      </td>
                      <td>{patron.firstName}</td>
                      <td>{patron.lastName}</td>
                      <td>{patron.email}</td>
                      <td>
                        {patron.isQuickCreated ? (
                          <span className="nfc-type-badge nfc-type-na" title="Not eligible for NFC mobile credentials">
                            N/A
                          </span>
                        ) : (
                          <span
                            className={`nfc-type-badge nfc-type-${(patron.nfcCredentialType || "40X").toLowerCase()}`}
                            title={`NFC Credential Type: ${patron.nfcCredentialType || "40X"}`}
                          >
                            {patron.nfcCredentialType || "40X"}
                          </span>
                        )}
                      </td>
                      <td>{patron.mobileNumber}</td>
                      <td>{new Date(patron.createdAt).toLocaleDateString()}</td>
                      <td className="actions-cell">
                        <button
                          className="view-credentials-button"
                          onClick={() => handleViewCredentials(patron)}
                          title="View Credentials"
                        >
                          🎫
                        </button>
                        <button
                          className="view-credentials-button"
                          onClick={() => {
                            setPatronForPhysicalCred(patron);
                            setShowAddPhysicalCredentialModal(true);
                          }}
                          title="Add Physical Credential"
                        >
                          🆕
                        </button>
                        <button
                          className="edit-button"
                          onClick={() => handleEditPatron(patron)}
                          title="Edit Patron"
                        >
                          ✏️
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeletePatron(patron)}
                          title="Delete Patron"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination-controls">
            <button
              className="pagination-button"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {currentPage} • {patrons.length} patron
              {patrons.length !== 1 ? "s" : ""}
            </span>
            <button
              className="pagination-button"
              onClick={handleNextPage}
              disabled={!hasMore}
            >
              Next →
            </button>
          </div>
        </>
      )}

      {isFormOpen && (
        <PatronForm
          patron={editingPatron}
          orgId={organizationId}
          organizationName={organizationName}
          organizations={organizations}
          existingPatrons={patrons}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {selectedPhoto && (
        <div className="photo-modal" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-modal-content">
            <img src={selectedPhoto} alt="Patron Badge" />
            <button
              className="photo-modal-close"
              onClick={() => setSelectedPhoto(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {showCredentialsModal && selectedPatron && (
        <PatronCredentialsModal
          patron={selectedPatron}
          devices={devices}
          onClose={handleCloseCredentialsModal}
          pendingDeletionCredentials={pendingDeletionCredentials}
          setPendingDeletionCredentials={setPendingDeletionCredentials}
          pendingAdditionCredentials={pendingAdditionCredentials}
          setPendingAdditionCredentials={setPendingAdditionCredentials}
          pendingFullCredentialDeletions={pendingFullCredentialDeletions}
          setPendingFullCredentialDeletions={setPendingFullCredentialDeletions}
          devStreamEvents={devStreamEvents}
          lastCredentialDownload={lastCredentialDownload}
          lastCredentialProvisioned={lastCredentialProvisioned}
          lastCredentialRemoved={lastCredentialRemoved}
        />
      )}

      <ConfirmActionModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, patron: null })}
        onConfirm={handleConfirmDeletePatron}
        title="🗑️ Delete Patron"
        message={`Are you sure you want to PERMANENTLY DELETE ${
          deleteConfirmModal.patron?.firstName || ""
        } ${deleteConfirmModal.patron?.lastName || ""}?

This will:
• Delete the patron from the database
• Remove ALL credentials from their devices
• Remove credentials from their phone/watch

This action CANNOT be undone.`}
        confirmButtonText="Delete Patron"
        isDestructive={true}
        isProcessing={isDeletingPatron}
        requireTypedConfirmation={true}
        confirmationWord={deleteConfirmModal.patron?.email || ""}
        caseSensitive={false}
        details={
          deleteConfirmModal.patron && (
            <>
              <p>
                <strong>Email:</strong> {deleteConfirmModal.patron.email}
              </p>
              {deleteConfirmModal.patron.mobileNumber && (
                <p>
                  <strong>Mobile:</strong>{" "}
                  {deleteConfirmModal.patron.mobileNumber}
                </p>
              )}
              {deleteConfirmModal.patron.patronNumber && (
                <p>
                  <strong>Patron Number:</strong>{" "}
                  {deleteConfirmModal.patron.patronNumber}
                </p>
              )}
            </>
          )
        }
      />

      <PatronLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        organizationName={organizationName}
        patronLimit={config.patronLimit}
      />

      {showAddPhysicalCredentialModal && patronForPhysicalCred && (
        <AddPhysicalCredentialModal
          patronId={patronForPhysicalCred.patronId}
          orgId={patronForPhysicalCred.orgId}
          onClose={() => {
            setShowAddPhysicalCredentialModal(false);
            setPatronForPhysicalCred(null);
          }}
          onCredentialCreated={() => {
            setShowAddPhysicalCredentialModal(false);
            setPatronForPhysicalCred(null);
          }}
          getAccessToken={getAccessToken}
        />
      )}

      {isQuickCreateOpen && (
        <QuickCreateCredentialModal
          organizationId={organizationId}
          getAccessToken={getAccessToken}
          onSuccess={() => {
            setIsQuickCreateOpen(false);
            loadPatrons();
          }}
          onClose={() => setIsQuickCreateOpen(false)}
        />
      )}
    </div>
  );
};

export default PatronManagement;
